import PropTypes from 'prop-types';
import React from 'react';

import * as Sentry from '@sentry/browser';

import { useHistory } from 'react-router-dom';
import i18n from 'i18next';
import { useTranslation } from 'react-i18next';
import { useForm, requiredField } from '../../utils/validators';
import Loading from '../../components/loading';
import EventCreationInfos from './eventCreationInfos';
import EventCreationLocalization from './eventCreationLocalization';
import EventCreationDates from './eventCreationDates';
import EventCreationRates from './eventCreationRates';
import EventModel from '../../models/eventModel';
import SubtypeEventModel from '../../models/subtypeEventModel';
import { ToastContext, Button, Card, Message, Modal } from '@weezevent/nacre';
import moment from 'moment-timezone';
import countries from 'i18n-iso-countries';

import Styles from '../../styles/eventCreation/eventCreation.module.css';
import Minisite from '../../models/minisite';
import { useSegment } from '../../components/tracking/trackingProvider';
import { allowExitingEventCreation, trackRateType } from './utils';
import { capitalizeFirstLetter } from '../attendee/detail/components/utils';
import { websiteFieldOnlineEvent } from '../events/config/eventForm';
import { LANGUAGES } from '../../components/translations/translationModal';

import { initLanguage, handleTranslations } from './translationsUtils';

export function HiddenTextLocalization({ formEvent, communicationPlatformOptions }) {
    const { t } = useTranslation();

    if (formEvent?.localization_type === 'location') {
        return (
            <span>
                {formEvent?.address?.name && (
                    <span>
                        {t('ticket.creationEvent.localization.hiddenTextLabelName')} <span className={Styles['hidden-text-bold']}>{formEvent?.address?.name}</span>
                    </span>
                )}{' '}
                {formEvent?.full_address && (
                    <span>
                        {t('ticket.creationEvent.localization.hiddenTextLabelPlace')} <span className={Styles['hidden-text-bold']}>{formEvent?.full_address}</span>
                    </span>
                )}
            </span>
        );
    }

    if (formEvent?.localization_type === 'online') {
        return (
            <span>
                {formEvent?.communication_platform && (
                    <span>
                        {t('ticket.creationEvent.localization.hiddenTextLabelPlatform')}{' '}
                        <span className={Styles['hidden-text-bold']}>
                            {t(communicationPlatformOptions.filter(platform => platform.value === formEvent?.communication_platform)[0]?.text)}
                        </span>
                    </span>
                )}
                {' — '}
                {formEvent?.website && (
                    <span>
                        {t('ticket.creationEvent.localization.hiddenTextLabelLink')} <span className={Styles['hidden-text-bold']}>{formEvent?.website}</span>
                    </span>
                )}
            </span>
        );
    }

    return <></>;
}

HiddenTextLocalization.propTypes = {
    formEvent: PropTypes.object.isRequired,
    communicationPlatformOptions: PropTypes.array.isRequired
};

function SaveAndRedirectButton({ hideButton, performAction, loading, disabled = false }) {
    const { t } = useTranslation();
    const [showModal, setShowModal] = React.useState(false);
    if (hideButton || !performAction) {
        return null;
    }

    return (
        <>
            <Modal
                open={showModal}
                onClose={() => setShowModal(false)}
                title={t('ticket.creationEvent.noPriceModal.title')}
                submitButton={
                    <Button
                        primary
                        onClick={() => {
                            performAction();
                            setShowModal(false);
                        }}
                        label={t('ticket.common.action.confirm')}
                        disabled={disabled}
                        isLoading={loading}
                    />
                }
                cancelButton={<Button onClick={() => setShowModal(false)} label={t(`ticket.common.action.cancel`)} />}
            >
                {t('ticket.creationEvent.noPriceModal.body')}
            </Modal>
            <Button
                className={Styles['button-save-configure']}
                label={t('ticket.creationEvent.saveAndRedirect')}
                isLoading={loading}
                onClick={() => setShowModal(true)}
                disabled={disabled}
            ></Button>
        </>
    );
}
SaveAndRedirectButton.propTypes = {
    hideButton: PropTypes.bool.isRequired,
    performAction: PropTypes.func.isRequired,
    loading: PropTypes.bool.isRequired,
    disabled: PropTypes.bool
};

export function HiddenTextDates({ formEvent }) {
    const { t } = useTranslation();

    let startDate = new Date(formEvent?.start_date);
    let startTime = startDate.toLocaleTimeString(i18n.language, { hour: '2-digit', minute: '2-digit', timeZone: formEvent.timezone });
    startDate = startDate.toLocaleDateString(i18n.language, { day: '2-digit', month: '2-digit', year: 'numeric', timeZone: formEvent.timezone });

    if (formEvent?.end_date) {
        let endDate = new Date(formEvent?.end_date);
        let endTime = endDate.toLocaleTimeString(i18n.language, { hour: '2-digit', minute: '2-digit', timeZone: formEvent.timezone });
        endDate = endDate.toLocaleDateString(i18n.language, { day: '2-digit', month: '2-digit', year: 'numeric', timeZone: formEvent.timezone });

        return t('ticket.creationEvent.dates.hiddenTextDatesWithEnd', { startDate, startTime, endDate, endTime });
    }

    return t('ticket.creationEvent.dates.hiddenTextDates', { startDate, startTime });
}

HiddenTextDates.propTypes = {
    formEvent: PropTypes.object.isRequired
};

export default function EventCreationForm({ organization, steps, onChangeSteps, organizationInfos, scrollTop = () => {} }) {
    const history = useHistory();
    const { t } = useTranslation();
    const toast = React.useContext(ToastContext);
    const { track } = useSegment();

    const [isSubmitting, setSubmitting] = React.useState(false);
    const [autoMode, setAutoMode] = React.useState(true);

    const [eventSubtypes, loading] = SubtypeEventModel.useApiModel({
        organizationId: organization.id
    });

    const subtypesOptions = React.useMemo(() => {
        return eventSubtypes?.map(subtype => ({ text: subtype.label, value: subtype.type, external_id: subtype.id_weez }));
    }, [eventSubtypes]);

    const dateTwoDays = React.useMemo(() => {
        return moment
            .utc()
            .add(3, 'hours')
            .set('minute', 0)
            .set('second', 0)
            .format();
    }, []);

    const languageOptions = React.useMemo(() => {
        let list = organization;
        return list.languages.map(lang => {
            return {
                label: LANGUAGES[lang.toLowerCase()],
                value: lang
            };
        });
    }, [organization]);

    const allowedLanguages = React.useMemo(() => organization.languages.map(l => l.toLowerCase()), [organization]);

    const [event, setEvent] = React.useState({});

    const [formEvent, setFormEvent, setFormEventObject, formErrors, eventValidate] = useForm(
        {
            name: '',
            subtype: null,
            description: '',
            default_language: null,
            localization_type: 'location',
            address: {
                name: '',
                address1: '',
                address2: '',
                city: '',
                country: '',
                state: '',
                zip_code: ''
            },
            address_auto_mode: autoMode,
            publication_status: 'draft',
            full_address: null,
            communication_platform: null,
            website: '',
            use_sessions: false,
            full_picture: null,
            start_date: dateTwoDays,
            end_date: null,
            timezone: organization?.timezone || 'Europe/Paris',
            rate: null,
            tax_id: null,
            translations: {
                description: {}
            }
        },
        {
            name: requiredField,
            subtype: requiredField,
            website: websiteFieldOnlineEvent
        }
    );

    const defaultLanguage = React.useMemo(() => {
        if (formEvent?.default_language && organization.languages.includes(formEvent.default_language)) {
            return formEvent.default_language;
        }
        const orgaDefault = organization.default_locale.replace('_', '-').toLowerCase();
        return organization.languages.includes(orgaDefault) ? orgaDefault : organization.languages[0];
    }, [formEvent, organization, languageOptions]);

    const lang = React.useMemo(() => {
        return i18n.language.split('-')[0];
    }, [i18n]);

    const getCountryLabel = React.useCallback(
        code => {
            if (!lang || !code) {
                return '';
            }

            return countries.getName(code, lang, { select: 'official' });
        },
        [lang]
    );

    React.useEffect(() => {
        if (!organization.onboarding_date && organizationInfos.event) {
            let evt = organizationInfos.event;
            let fullAddress =
                (evt.address.address1 ? evt.address.address1 + ' ' : '') +
                (evt.address.zip_code ? evt.address.zip_code + ' ' : '') +
                (evt.address.city ? evt.address.city + ' ' : '') +
                getCountryLabel(evt.address.country);

            setFormEventObject({ ...organizationInfos.event, full_address: fullAddress }, true);
        }
    }, [organization, organizationInfos, setFormEventObject, getCountryLabel]);

    const getEventTypeLabel = React.useMemo(() => {
        return subtypesOptions?.find(subtypeOption => subtypeOption.value === formEvent?.subtype)?.text;
    }, [formEvent, subtypesOptions]);

    const communicationPlatformOptions = [
        {
            text: 'Cisco Webex',
            value: 'Cisco Webex'
        },
        {
            text: 'Google Meet',
            value: 'Google Meet'
        },
        {
            text: 'Microsoft Teams',
            value: 'Microsoft Teams'
        },
        {
            text: 'Skype',
            value: 'Skype'
        },
        {
            text: 'Zoom',
            value: 'Zoom'
        },
        {
            text: t('ticket.common.other'),
            value: 'Other'
        }
    ];

    const RATE_TYPES = {
        fixed: {
            type: 'fixed',
            price: 10,
            name: t('ticket.creationEvent.rates.priceNamePaid'),
            inventories: [],
            tax_id: null
        },
        free: {
            type: 'free',
            price: 0,
            name: t('ticket.creationEvent.rates.priceNameFree'),
            inventories: [],
            tax_id: 0
        },
        open: {
            type: 'open',
            price: 10,
            name: t('ticket.creationEvent.rates.priceNameDonation'),
            inventories: [],
            tax_id: 0
        }
    };

    const saveEventAndGenerateMinisite = React.useCallback(
        ({ eventObject, onDone }) => {
            return eventObject
                .save({ organizationId: organization.id })
                .then(response => {
                    // toast.success(t(`ticket.creationEvent.createSuccess`));
                    setFormEventObject(response.response, true);
                    onDone(response);
                    const minisite = new Minisite();
                    minisite
                        .generate({ organizationId: organization.id, eventId: response.response.id })
                        .then(r => {
                            setFormEventObject({ site: r.id, gemino_url: r.url }, true);
                            eventObject.site = r.id;
                            eventObject.gemino_url = r.url;
                            eventObject
                                .save({
                                    organizationId: organization.id
                                })
                                .catch(e => {
                                    console.log(e);
                                });
                        })
                        .catch(e => {
                            Sentry.captureException(e);
                            console.error(e);
                        });
                })
                .catch(e => {
                    Sentry.captureException(e);
                    toast.error(t(`ticket.creationEvent.createError`));
                })
                .finally(() => setSubmitting(false));
        },
        [organization]
    );

    const saveEvent = React.useCallback(
        ({ eventObject, onDone }) => {
            return eventObject
                .save({ organizationId: organization.id })
                .then(response => {
                    // toast.success(t(`ticket.creationEvent.createSuccess`));
                    setFormEventObject(response.response, true);
                    onDone(response);
                })
                .catch(e => {
                    Sentry.captureException(e);
                    console.error(e);
                    toast.error(t(`ticket.creationEvent.createError`));
                })
                .finally(() => setSubmitting(false));
        },
        [organization]
    );

    const handleSubmit = React.useCallback(
        (onDone = () => {}, toPublish = false) => {
            setSubmitting(true);
            let formValid = eventValidate();
            if (formValid) {
                let eventObject = new EventModel(formEvent);
                if (toPublish) {
                    eventObject.publication_status = 'published';
                    //Publish minisite if event must be to publish
                    eventObject.publish_default_site = true;
                }

                eventObject.publication_status = toPublish ? 'published' : 'draft';
                if (!eventObject.rate) {
                    eventObject.rate = null;
                } else {
                    eventObject.tax_id = eventObject.rate.tax_id === 0 ? null : eventObject.rate.tax_id;
                    delete eventObject.rate.tax_id;
                }

                //Do not send chart, because the return of the "PATCH Event" does not return the updated chart.
                //So we only send it to let the backend manage
                if (!eventObject.chart) {
                    delete eventObject.chart;
                }

                if (!eventObject.address) {
                    delete eventObject.address;
                } else {
                    if (Object.values(eventObject.address).every(item => !item)) {
                        eventObject.address = null;
                    }
                }

                if (eventObject.mustGenerateMinisite()) {
                    saveEventAndGenerateMinisite({ eventObject, onDone });
                } else {
                    saveEvent({ eventObject, onDone });
                }
            } else {
                toast.error(t(`ticket.creationEvent.form_error`));
                setSubmitting(false);
            }
        },
        [setSubmitting, organization, eventValidate, formEvent, saveEvent, saveEventAndGenerateMinisite, defaultLanguage]
    );

    const handleTrackLocalization = React.useMemo(() => {
        if (formEvent.localization_type === 'no_location') {
            return 'None';
        }
        return capitalizeFirstLetter(formEvent.localization_type);
    }, [formEvent, capitalizeFirstLetter]);

    const initLanguageEvent = React.useCallback((value, name) => initLanguage(value, name, formEvent, setFormEventObject), [formEvent, setFormEventObject]);

    const handleTranslationsEvent = React.useCallback(
        (value, property, selectedLanguage) => handleTranslations(value, property, selectedLanguage, formEvent, setFormEventObject, defaultLanguage, event?.translations),
        [setFormEventObject, formEvent, defaultLanguage, allowedLanguages, event]
    );

    if (loading) {
        return <Loading />;
    }

    return (
        <Card.FormManager onChangeSteps={onChangeSteps} steps={steps} labelStep={t('ticket.common.step.current')} labelPreviousStep={t('ticket.common.back_step')}>
            <Card.FormStep
                title={t('ticket.creationEvent.infos.title')}
                onEdit={() => {
                    track('ACTION > EDIT AGAIN INFORMATION', { 'Page Title': 'CREATE EVENT' });
                }}
                editLabel={t('ticket.common.action.edit')}
                subtitle={t('ticket.creationEvent.infos.subtitle')}
                summary={
                    formEvent?.name?.length !== 0 && formEvent?.subtype !== null ? (
                        <span>
                            {t('ticket.creationEvent.infos.hiddenTextLabelName')} <span className={Styles['hidden-text-bold']}>{formEvent?.name}</span> —{' '}
                            {t('ticket.creationEvent.infos.hiddenTextLabelSubtype')} <span className={Styles['hidden-text-bold']}>{getEventTypeLabel}</span>
                        </span>
                    ) : null
                }
                rightComponent={
                    <Message type="tips" title={t('ticket.common.tips')} mini>
                        {t('ticket.creationEvent.infos.tips')}
                    </Message>
                }
            >
                <EventCreationInfos
                    formEvent={formEvent}
                    organization={organization}
                    onDone={onCardDone => {
                        handleSubmit(response => {
                            setEvent(response.response);
                            onCardDone();
                            scrollTop();
                        });
                        let subtypesOption = subtypesOptions?.find(subtypeOption => subtypeOption.value === formEvent?.subtype);
                        track('ACTION > VALIDATE STEP INFORMATIONS', {
                            'Event type ID': subtypesOption?.external_id,
                            'Event type name': subtypesOption?.text,
                            'Page Title': 'CREATE EVENT'
                        });
                    }}
                    onChange={setFormEvent}
                    isSubmitting={isSubmitting}
                    onChangeObject={info => setFormEventObject(info, true)}
                    subtypesOptions={subtypesOptions}
                    rateTypes={RATE_TYPES}
                    defaultLanguage={defaultLanguage}
                    languageOptions={languageOptions}
                    initLanguage={initLanguageEvent}
                    handleTranslations={handleTranslationsEvent}
                >
                    <SaveAndRedirectButton
                        key={'info'}
                        performAction={() => {
                            handleSubmit(response => {
                                history.push(`/ticket/O${organization.id}/E${response.response.id}/settings/event`);
                            });

                            let subtypesOption = subtypesOptions?.find(subtypeOption => subtypeOption.value === formEvent?.subtype);
                            track('ACTION > SAVE STEP INFORMATIONS', {
                                'Event type ID': subtypesOption?.external_id,
                                'Event type name': subtypesOption?.text,
                                'Page Title': 'CREATE EVENT'
                            });
                        }}
                        hideButton={!allowExitingEventCreation({ organization, organizationInfos })}
                        loading={isSubmitting}
                    />
                </EventCreationInfos>
            </Card.FormStep>
            <Card.FormStep
                title={t('ticket.creationEvent.localization.title')}
                onEdit={() => {
                    setAutoMode(formEvent.address_auto_mode);
                    track('ACTION > EDIT AGAIN PLACE', { 'Page Title': 'CREATE EVENT' });
                }}
                editLabel={t('ticket.common.action.edit')}
                subtitle={t('ticket.creationEvent.localization.subtitle')}
                summary={<HiddenTextLocalization formEvent={formEvent} communicationPlatformOptions={communicationPlatformOptions} />}
                rightComponent={
                    <Message type="tips" title={t('ticket.common.tips')} mini>
                        {t('ticket.creationEvent.localization.tips')}
                    </Message>
                }
            >
                <EventCreationLocalization
                    formEvent={formEvent}
                    onDone={onCardDone => {
                        handleSubmit(() => {
                            setFormEvent('address_auto_mode', autoMode);
                            onCardDone();
                            scrollTop();
                        });
                        track('ACTION > VALIDATE STEP PLACE', {
                            'Page Title': 'CREATE EVENT',
                            'Place type': handleTrackLocalization,
                            'Place Name': formEvent.address?.name
                        });
                    }}
                    onChange={setFormEvent}
                    isSubmitting={isSubmitting}
                    onChangeObject={address => setFormEventObject(address, true)}
                    platformOptions={communicationPlatformOptions}
                    autoMode={autoMode}
                    setAutoMode={setAutoMode}
                    errors={formErrors}
                >
                    <SaveAndRedirectButton
                        key={'localization'}
                        performAction={() => {
                            track('ACTION > SAVE STEP PLACE', {
                                'Page Title': 'CREATE EVENT',
                                'Place Type': handleTrackLocalization,
                                'Place Name': formEvent.address?.name
                            });
                            handleSubmit(response => {
                                history.push(`/ticket/O${organization.id}/E${response.response.id}/settings/event`);
                            });
                        }}
                        hideButton={!allowExitingEventCreation({ organization, organizationInfos })}
                        loading={isSubmitting}
                    />
                </EventCreationLocalization>
            </Card.FormStep>
            <Card.FormStep
                title={t('ticket.creationEvent.dates.title')}
                summary={
                    <span className={Styles['hidden-text-bold']}>
                        <HiddenTextDates formEvent={formEvent} />
                    </span>
                }
                onEdit={() => {
                    track('ACTION > EDIT AGAIN DATE', { 'Page Title': 'CREATE EVENT' });
                }}
                editLabel={t('ticket.common.action.edit')}
                subtitle={t('ticket.creationEvent.dates.subtitle')}
                rightComponent={
                    <Message type="tips" title={t('ticket.common.tips')} mini>
                        {t('ticket.creationEvent.dates.tips')}
                    </Message>
                }
            >
                <EventCreationDates
                    formEvent={formEvent}
                    onDone={onCardDone => {
                        track('ACTION > VALIDATE STEP DATE', { 'Page Title': 'CREATE EVENT', 'Date type': formEvent?.use_sessions ? 'Sessions' : 'One' });
                        handleSubmit(() => {
                            onCardDone();
                            scrollTop();
                        });
                    }}
                    onChangeObject={dates => setFormEventObject(dates, true)}
                    isSubmitting={isSubmitting}
                    onChange={setFormEvent}
                >
                    <SaveAndRedirectButton
                        key={'dates'}
                        performAction={() => {
                            track('ACTION > SAVE STEP DATE', { 'Page Title': 'CREATE EVENT', 'Date type': formEvent?.use_sessions ? 'Sessions' : 'One' });
                            handleSubmit(response => {
                                history.push(`/ticket/O${organization.id}/E${response.response.id}/settings/event`);
                            });
                        }}
                        hideButton={!allowExitingEventCreation({ organization, organizationInfos })}
                        loading={isSubmitting}
                    />
                </EventCreationDates>
            </Card.FormStep>
            <Card.FormStep
                title={t('ticket.creationEvent.rates.title')}
                subtitle={t('ticket.creationEvent.rates.subtitle')}
                editLabel={t('ticket.common.action.edit')}
                rightComponent={
                    <Message type="tips" title={t('ticket.common.tips')} mini>
                        {t('ticket.creationEvent.rates.tips')}
                    </Message>
                }
            >
                <EventCreationRates
                    onChange={setFormEvent}
                    onDone={toPublish => {
                        handleSubmit(response => {
                            let subtypeOption = subtypesOptions?.find(subtypeOption => subtypeOption.value === formEvent?.subtype);
                            let peopleParams = null;
                            let values = {
                                'Page Title': 'CREATE EVENT',
                                'Event Type name': subtypeOption.text,
                                'Event Type ID': subtypeOption.external_id,
                                'Event Timezone': formEvent.timeZone,
                                'Event Start Date': new Date(formEvent.start_date).toUTCString(),
                                'Event Name': formEvent.name,
                                'Event ID': formEvent.id,
                                'Event End Date': formEvent.end_date ? new Date(formEvent.end_date).toUTCString() : null,
                                'Rate type': trackRateType(formEvent.rate?.type),
                                'Place type': handleTrackLocalization,
                                'Ticket Price': formEvent.rate?.price,
                                Currency: organization.currency
                            };

                            if (toPublish) {
                                values['Event Published Count'] = 1;

                                peopleParams = {
                                    'Event Published List': [formEvent.id],
                                    'Last Event Published Date': new Date().toUTCString()
                                };
                            }

                            track(`ACTION > ${toPublish ? 'PUBLISH EVENT' : 'CREATE EVENT AND CONTINUE'}`, values, peopleParams);

                            history.push(`/ticket/O${organization.id}/E${response.response.id}` + (toPublish ? '/done' : ''));
                        }, toPublish);
                    }}
                    formEvent={formEvent}
                    isSubmitting={isSubmitting}
                    onSubmitting={setSubmitting}
                    organization={organization}
                    rateTypes={RATE_TYPES}
                    organizationInfos={organizationInfos}
                />
            </Card.FormStep>
        </Card.FormManager>
    );
}

EventCreationForm.propTypes = {
    organization: PropTypes.object.isRequired,
    steps: PropTypes.object.isRequired,
    onChangeSteps: PropTypes.func.isRequired,
    organizationInfos: PropTypes.object.isRequired,
    scrollTop: PropTypes.func
};
